import { type ComponentType, type ReactNode } from 'react';
import { LuSparkle } from 'react-icons/lu';
import { BsGraphUp } from 'react-icons/bs';
import { RiEditLine, RiFileCheckLine, RiStethoscopeLine } from 'react-icons/ri';
import type { AgentType } from '@/model/Agent.model';
import type { FeatureFlagType } from '@/model/FeatureFlag.model';

type IconComponent = ComponentType<{ size: number; className: string }>;

const agentIconMap: Record<AgentType, IconComponent> = {
  study_health_check: RiStethoscopeLine,
  study_protocol_parser: RiEditLine,
  send_validator: RiFileCheckLine,
  benchling_core_analysis: BsGraphUp,
};

/**
 * Maps each agent type to its kebab-case URL segment. The API exposes agent
 * slugs as snake_case enum values, but web routes use kebab-case, so this map
 * is the single source of truth for translating between the two.
 */
export const agentUrlSlugMap: Record<AgentType, string> = {
  study_health_check: 'study-health-check',
  study_protocol_parser: 'study-design-agent',
  send_validator: 'send-validator',
  benchling_core_analysis: 'core-analysis',
};

const urlSlugToAgentType = new Map<string, AgentType>(
  (Object.entries(agentUrlSlugMap) as Array<[AgentType, string]>).map(([type, urlSlug]) => [urlSlug, type])
);

/** Resolves a kebab-case URL segment back to its agent type, or undefined if unknown. */
export const parseAgentUrlSlug = (urlSlug: string): AgentType | undefined => urlSlugToAgentType.get(urlSlug);

/** Per-agent display strings for the session view: the friendly `name` and the in-progress `title`. */
interface AgentDisplay {
  /** Human-friendly agent name (shown in the session breadcrumb). */
  name: string;
  /** What the agent is doing, shown as the root node's title. */
  title: string;
}

/**
 * Display strings per agent type, used where only the agent slug is available (e.g. the session page,
 * which gets `agent_type` from the URL but no display copy). The `title` is per-agent because each
 * agent does something different.
 */
export const agentDisplayMap: Record<AgentType, AgentDisplay> = {
  study_health_check: { name: 'Study Health Check', title: 'Generating Report' },
  study_protocol_parser: { name: 'Study Design Agent', title: 'Parsing Protocol' },
  send_validator: { name: 'SEND Validator', title: 'Validating Domain' },
  benchling_core_analysis: { name: 'Analysis Agent', title: 'Analyzing in Benchling' },
};

/**
 * Browser tab titles per agent. Kept separate from `agentDisplayMap` because tab titles are
 * sentence case, while the breadcrumbs and hub cards render the title-case product names.
 */
export const agentPageTitleMap: Record<AgentType, string> = {
  study_health_check: 'Study health check',
  study_protocol_parser: 'Study design agent',
  send_validator: 'SEND Validator',
  benchling_core_analysis: 'Analysis agent',
};

/**
 * Returns the icon element associated with an agent slug.
 * Falls back to a sparkle icon for unrecognized slugs.
 */
export const getAgentIcon = (slug: AgentType): ReactNode => {
  const Icon = agentIconMap[slug] ?? LuSparkle;
  return <Icon size={26} className="blue" />;
};

/**
 * Maps each agent type to the feature flag that gates it. Agent slugs and flag names do
 * not always match (e.g. `study_protocol_parser` is gated by `study_design_agent`), so
 * this map is the single source of truth for that relationship. The `Record<AgentType, …>`
 * type ensures every agent declares a gating flag.
 */
const agentFlagMap: Record<AgentType, FeatureFlagType> = {
  study_health_check: 'study_health_check',
  study_protocol_parser: 'study_design_agent',
  send_validator: 'send_validator',
  benchling_core_analysis: 'benchling_core_analysis',
};

/**
 * Agents that run on Benchling Core as the user, rather than on our harness. Keyed by slug for now; an
 * agent definition flag would be the durable home for this.
 */
const BENCHLING_CORE_AGENTS: ReadonlySet<AgentType> = new Set<AgentType>(['benchling_core_analysis']);

export const isBenchlingCoreAgent = (slug: AgentType): boolean => BENCHLING_CORE_AGENTS.has(slug);

/**
 * Determines whether an agent should be visible/accessible. An agent is enabled only when
 * the master `ai_agents` flag is on AND the agent's own feature flag is on. Used to gate
 * the hub cards and the agent setup/session routes.
 */
export const isAgentEnabled = (features: Partial<Record<FeatureFlagType, boolean>>, slug: AgentType): boolean =>
  features.ai_agents === true && features[agentFlagMap[slug]] === true;
